import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { CheckCircle, Warning, ArrowRight } from "@phosphor-icons/react";

const MAX_POLLS = 8;

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sid = params.get("session_id");
  const { refresh } = useAuth();
  const [state, setState] = useState({ status: "checking", polls: 0 });

  useEffect(() => {
    if (!sid) { setState({ status: "error" }); return; }
    let cancelled = false;
    async function poll(count) {
      try {
        const { data } = await api.get(`/payments/status/${sid}`);
        if (cancelled) return;
        if (data.payment_status === "paid") {
          await refresh();
          setState({ status: "paid", data });
        } else if (["failed", "expired"].includes(data.payment_status)) {
          setState({ status: "failed", data });
        } else if (count >= MAX_POLLS) {
          setState({ status: "timeout", data });
        } else {
          setTimeout(() => poll(count + 1), 2000);
        }
      } catch {
        if (!cancelled) setState({ status: "error" });
      }
    }
    poll(0);
    return () => { cancelled = true; };
  }, [sid, refresh]);

  return (
    <div className="min-h-screen bg-brand-bg dot-bg flex items-center justify-center p-6" data-testid="payment-success-page">
      <div className="bg-white border border-brand-border rounded-3xl p-10 max-w-md w-full text-center shadow-[0_10px_40px_rgba(44,42,41,0.06)]">
        {state.status === "checking" && (
          <>
            <div className="mx-auto w-14 h-14 rounded-full border-4 border-brand-primary/20 border-t-brand-primary animate-spin" />
            <h1 className="font-heading text-2xl font-bold mt-6">Confirming your payment…</h1>
            <p className="text-brand-text/70 mt-2">Give us a moment.</p>
          </>
        )}
        {state.status === "paid" && (
          <>
            <CheckCircle size={64} weight="duotone" className="text-brand-success mx-auto" />
            <h1 className="font-heading text-3xl font-bold mt-4 tracking-tight" data-testid="paid-headline">Payment successful</h1>
            <p className="text-brand-text/70 mt-2">Your Vendorly account is active for 1 month.</p>
            <Link to="/app" className="mt-8 inline-flex items-center gap-2 bg-brand-primary hover:bg-brand-primaryHover text-white rounded-full px-6 py-3 font-semibold transition-colors" data-testid="paid-continue">
              Go to dashboard <ArrowRight size={18} weight="bold" />
            </Link>
          </>
        )}
        {(state.status === "timeout" || state.status === "error" || state.status === "failed") && (
          <>
            <Warning size={64} weight="duotone" className="text-brand-warning mx-auto" />
            <h1 className="font-heading text-2xl font-bold mt-4">Still processing</h1>
            <p className="text-brand-text/70 mt-2">If you completed payment, it may take a minute to reflect. Check billing to see status.</p>
            <Link to="/app/billing" className="mt-8 inline-flex items-center gap-2 border border-brand-border rounded-full px-6 py-3 font-semibold hover:bg-brand-surface" data-testid="paid-recheck">
              Go to Billing
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
